import React, { useState, useEffect, useRef, useCallback, MutableRefObject } from 'react';
import { reaction } from 'mobx';
import { IEditorPasteProps, Off, PanelType } from '../interface';
import { pasteSpecialEventBus } from '@src/slide/application/service/copy-paste/core/paste/paste-special/event-bus';
import { ICopyInfo } from '@src/slide/domain/ui-model/element-modeles/property/copy/copy-model/paste-special.interface';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import PasteSpecialPanel from '../index';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';

/**
 * 页内元素复制，选择性粘贴面板
 */
const EditorPasteSpecialPanel = (props: IEditorPasteProps) => {
    const {
        serviceDelegates,
        serviceDelegates: {
            selectionService,
            editingStateManager,
            masterLayoutEditService,
        },
    } = props;

    const [isShow, setShow] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [firstId, setFirstId] = useState<string | undefined>('');
    const copyInfoRef = useRef({}) as MutableRefObject<ICopyInfo>;
    const undoRedoRef = useRef(() => {}) as MutableRefObject<Off>;
    const menuClickStatusRef = useRef<boolean>(false);
    const getPosition = () => {
        const { firstElementDataId } = copyInfoRef.current;
        if (firstId === firstElementDataId) return position;
        const currentSlideModel = selectionService.currentSlide;
        const elementModel = currentSlideModel?.children?.find(elementModel => elementModel.id === firstElementDataId);
        if (!elementModel) return { top: 0, left: 0 };
        // props.style中的top和left值是相对于canvas画布的左上角，而非整个编辑区域的左上角
        // style中的值都是视觉像素值/scale得到的, canvasSize中的top和left都是视觉像素值（即真实看到的像素值）
        const { top = 0, left = 0, width = 0, height = 0 } = elementModel?.props?.style || {};
        return {
            top: Math.floor(((top + height) * props.scale) + props.canvasTop),
            left: Math.floor(((left + width) * props.scale) + props.canvasLeft),
        };
    };

    // 组件挂载后，注册事件
    useEffect(() => {
        // 监听粘贴完成，展示面板
        const disposePasteEvent = pasteSpecialEventBus.on(PanelType.EDIT_PASTE_PANEL, (copyInfo: ICopyInfo) => {
            copyInfoRef.current = copyInfo;
            setShow(true);
            setFirstId(copyInfo.firstElementDataId);

            // 粘贴形状，图标曝光埋点
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PASTE_SPECIAL,
                action: TDWActionType.SHAPE_EXPOSURE,
            });
        });

        // 监听元素操作，面板消失
        const disposeReaction = reaction(
            () => editingStateManager.isEditing,
            (isEditing) => {
                if (isEditing) {
                    setShow(false);
                }
            },
        );

        // 切页时，面板消失
        const disposeSlideIndex = reaction(
            () => selectionService.currentSlideIndex,
            () => {
                setShow(false);
            },
        );

        // 进入母版编辑后，面板消失
        const disposeMasterChange = reaction(
            () => masterLayoutEditService?.isMasterEditType,
            (isMasterEditType) => {
                if (!isMasterEditType) return;
                setShow(false);
            },
        );

        // 监听undoRedo
        undoRedoRef.current = onUndoRedo();
        return () => {
            // 组件销毁时取消监听
            disposePasteEvent();
            disposeReaction();
            disposeSlideIndex();
            disposeMasterChange();
            undoRedoRef.current?.();
        };
    }, []);


    // scale变化时，需重新计算位置
    useEffect(() => {
        if (!isShow) return;
        const position = getPosition();
        if (!position) return;
        setPosition(position);
    }, [props, isShow]);

    const onUndoRedo = (): Off => {
        const sdkCommandEvents = SlideDataServices.SDKDataServices.EventManager()?.commandEvents;
        return (
            sdkCommandEvents?.onUndoRedoChange(() => {
                // 菜单展示时，选中粘贴策略后会触发undo操作，但此时不应该隐藏按钮
                if (menuClickStatusRef.current) return;
                setShow(false);
            }) ?? (() => {})
        );
    };

    const beforePaste = useCallback(() => {
        menuClickStatusRef.current = true;
    }, []);

    const afterPaste = useCallback(() => {
        menuClickStatusRef.current = false;
    }, []);

    return isShow ? (
        <PasteSpecialPanel
            position={position}
            copyInfo={copyInfoRef.current}
            serviceDelegates={serviceDelegates}
            beforePaste={beforePaste}
            afterPaste={afterPaste}
        />
    ) : null;
};

export default React.memo(EditorPasteSpecialPanel);
